import React from "react";
import styled from "styled-components";
import defaultAuthorImage from "./default-author-image.svg";
import { formatDistanceToNow } from "date-fns"; // Ensure you install date-fns

type ArticleHeaderProps = {
  title: string;
  image?: string;
  author: {
    name: string;
    image?: string;
  };
  readingTime: string;
  publishDate: string;
  isWhiskymofoArticle: boolean;
  isVinomofoArticle: boolean;
};

// Utility to handle image URL transformations
const imageUrl = (url: string, options: { height?: number; width?: number; fit?: string; crop?: string }) => {
  // Placeholder for your image transformation logic
  return url;
};

// Styled components
const ArticleHeaderContainer = styled.div`
  --hero-height: 380px;
  --hero-height-large: 600px;
  --accent-color: #77b204;

  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
  font-family: "Graphik", sans-serif;
`;

const Hero = styled.div<{ backgroundImage: string }>`
  height: var(--hero-height-large);
  width: 100%;
  background-image: ${({ backgroundImage }) => `url(${backgroundImage})`};
  background-size: cover;
  background-position: center;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  position: relative;
  text-align: center;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 37%, rgba(0, 0, 0, 0.45) 92%);
  }
  @media (max-width: 767px) {
    height: var(--hero-height);
  }
  @media (min-width: 768px) {
    height: var(--hero-height-large);
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  line-height: 1.5;
  letter-spacing: 0.1em;
  color: white;
  position: relative;
  padding: 0 1em;
  z-index: 2;
  margin-bottom:2em;
  @media (max-width: 767px) {
    font-size: 2rem;
  }
  @media (min-width: 768px) {
    font-size: 4rem;
  }
`;

const Header = styled.div`
  margin-top: -36px;
  max-width: 760px;
  width: 100%;
  padding: 0 20px;
  text-align: center;
  z-index: 10;
`;

const Meta = styled.div`
  font-size: 1rem;
  color: #333;
`;

const AuthorImage = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid var(--accent-color);
  margin: 0 auto;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AuthorName = styled.div`
  margin-top: 0.5rem;
  strong {
    color: var(--accent-color);
  }
`;

const PublishDate = styled.div`
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: #888;
`;

const ReadingTime = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e2e2;
  position: relative;
  &::before {
    content: url("data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMTYgMTYiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+DQogIDxnIGZpbGw9IiM2MjY0NjgiIGZpbGwtcnVsZT0ibm9uemVybyI+DQogICAgPHBhdGggZD0iTTggMEMzLjYgMCAwIDMuNiAwIDhzMy42IDggOCA4IDgtMy42IDgtOC0zLjYtOC04LTh6bTAgMTRjLTMuMyAwLTYtMi43LTYtNnMyLjctNiA2LTYgNiAyLjcgNiA2LTIuNyA2LTYgNnoiLz4NCiAgICA8cGF0aCBkPSJNOSA0SDd2NWg1VjdIOXoiLz4NCiAgPC9nPg0KPC9zdmc+");
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
    opacity: 0.3;
    z-index: 10;
  }

  &::after {
    content: "";
    position: absolute;
    width: 40px;
    height: 40px;
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: #fff;
    opacity: 1;
`;

const ArticleHeader: React.FC<ArticleHeaderProps> = ({
  title,
  author,
  publishDate,
  readingTime,
  image,
}) => {
  return (
    <ArticleHeaderContainer>
      <Hero backgroundImage={imageUrl(image || "", { height: 722, fit: "crop" })}>
        <Title>{title}</Title>
      </Hero>

      <Header>
        <Meta>
          <AuthorImage>
            <img
              alt={author?.name}
              src={
                author?.image
                  ? imageUrl(author?.image, { height: 72, width: 72, fit: "crop", crop: "faces" })
                  : defaultAuthorImage
              }
            />
          </AuthorImage>
          <AuthorName>
            By <strong>{author.name}</strong>
          </AuthorName>
          <PublishDate>
            {formatDistanceToNow(new Date(publishDate), { addSuffix: true })}
          </PublishDate>
          <ReadingTime>{readingTime + " min read"}</ReadingTime>
        </Meta>
      </Header>
    </ArticleHeaderContainer>
  );
};

export default ArticleHeader;
